
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function AppPage() {
  const [activeTab, setActiveTab] = useState<'features' | 'screenshots' | 'reviews'>('features');
  const [downloadStats, setDownloadStats] = useState({
    totalDownloads: 0,
    activeUsers: 0,
    avgRating: 0
  });

  useEffect(() => {
    // Animate download stats
    const timer = setTimeout(() => {
      setDownloadStats({
        totalDownloads: 125000,
        activeUsers: 45000,
        avgRating: 4.8
      });
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const features = [
    {
      icon: 'ri-magic-line',
      title: 'AI Virtual Staging',
      description: 'Transform empty rooms with AI-powered furniture and decor in seconds',
      category: 'AI Technology'
    },
    {
      icon: 'ri-camera-line',
      title: 'Professional Photography',
      description: 'Enhance property photos with automatic lighting and color correction',
      category: 'Photography'
    },
    {
      icon: 'ri-delete-bin-line',
      title: 'Object Removal',
      description: 'Remove unwanted items from photos with advanced AI technology',
      category: 'AI Technology'
    },
    {
      icon: 'ri-sun-line',
      title: 'Day to Dusk',
      description: 'Convert daytime photos to stunning twilight shots automatically',
      category: 'Photography'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Mobile Optimized',
      description: 'Full-featured mobile app for on-the-go property management',
      category: 'Mobile'
    },
    {
      icon: 'ri-cloud-line',
      title: 'Cloud Sync',
      description: 'Access your projects anywhere with automatic cloud synchronization',
      category: 'Cloud'
    },
    {
      icon: 'ri-team-line',
      title: 'Team Collaboration',
      description: 'Share projects and collaborate with team members in real-time',
      category: 'Collaboration'
    },
    {
      icon: 'ri-bar-chart-line',
      title: 'Analytics Dashboard',
      description: 'Track performance metrics and property engagement statistics',
      category: 'Analytics'
    }
  ];

  const screenshots = [
    {
      id: 1,
      title: 'Home Dashboard',
      image: 'https://public.readdy.ai/ai/img_res/208085f9f5d1fcbc0dd3edab70c36532.jpg'
    },
    {
      id: 2,
      title: 'AI Staging',
      image: 'https://readdy.ai/api/search-image?query=mobile%20app%20interface%20showing%20AI%20virtual%20staging%20before%20after%20comparison%20real%20estate%20property%20photos%20furniture%20placement&width=300&height=600&seq=mobile-staging&orientation=portrait'
    },
    {
      id: 3,
      title: 'Photo Editor',
      image: 'https://readdy.ai/api/search-image?query=mobile%20photo%20editing%20app%20interface%20with%20AI%20enhancement%20tools%20real%20estate%20photography%20filters%20professional%20editing&width=300&height=600&seq=mobile-editor&orientation=portrait'
    },
    {
      id: 4,
      title: 'Property Manager',
      image: 'https://readdy.ai/api/search-image?query=mobile%20app%20property%20management%20interface%20listing%20details%20photos%20gallery%20real%20estate%20professional%20app%20design&width=300&height=600&seq=mobile-property&orientation=portrait'
    },
    {
      id: 5,
      title: 'Analytics',
      image: 'https://readdy.ai/api/search-image?query=mobile%20analytics%20dashboard%20interface%20with%20charts%20graphs%20real%20estate%20metrics%20performance%20data%20professional%20app%20design&width=300&height=600&seq=mobile-analytics&orientation=portrait'
    }
  ];

  const reviews = [
    {
      id: 1,
      name: 'Sarah Johnson',
      role: 'Real Estate Agent',
      company: 'Coldwell Banker',
      rating: 5,
      review: 'This app has revolutionized how I present properties. The AI staging feature saves me hours and my listings get 40% more views.',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20headshot%20woman%20real%20estate%20agent%20smiling%20confident%20business%20attire&width=80&height=80&seq=avatar-sarah&orientation=squarish'
    },
    {
      id: 2,
      name: 'Michael Chen',
      role: 'Property Developer',
      company: 'Chen Development Group',
      rating: 5,
      review: 'The mobile app makes it easy to manage multiple properties on the go. The photo enhancement tools are incredible.',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20headshot%20asian%20man%20business%20suit%20confident%20real%20estate%20developer&width=80&height=80&seq=avatar-michael&orientation=squarish'
    },
    {
      id: 3,
      name: 'Lisa Rodriguez',
      role: 'Broker',
      company: 'RE/MAX Premier',
      rating: 5,
      review: 'My clients love the virtual staging. It helps them visualize the potential of empty spaces. Sales have increased significantly.',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20headshot%20latina%20woman%20real%20estate%20broker%20professional%20attire%20confident%20smile&width=80&height=80&seq=avatar-lisa&orientation=squarish'
    }
  ];

  const companies = [
    { name: 'Coldwell Banker', logo: 'ri-building-line' },
    { name: 'RE/MAX', logo: 'ri-home-line' },
    { name: 'Keller Williams', logo: 'ri-community-line' },
    { name: 'Century 21', logo: 'ri-building-2-line' },
    { name: 'Sotheby\'s', logo: 'ri-ancient-gate-line' },
    { name: 'Compass', logo: 'ri-compass-line' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <i className="ri-smartphone-line text-blue-300"></i>
                <span className="text-sm font-medium">Now Available on Mobile</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                AI Realty Pro
                <span className="block text-blue-300">Mobile App</span>
              </h1>
              
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                Transform your real estate business with AI-powered staging, photo enhancement, and property management - all in your pocket.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a 
                  href="#" 
                  className="inline-flex items-center justify-center bg-black text-white px-6 py-4 rounded-xl font-semibold hover:bg-gray-800 transition-colors"
                >
                  <i className="ri-apple-line text-2xl mr-3"></i>
                  <div className="text-left">
                    <div className="text-xs">Download on the</div>
                    <div className="text-lg font-bold">App Store</div>
                  </div>
                </a>
                
                <a 
                  href="#" 
                  className="inline-flex items-center justify-center bg-black text-white px-6 py-4 rounded-xl font-semibold hover:bg-gray-800 transition-colors"
                >
                  <i className="ri-google-play-line text-2xl mr-3"></i>
                  <div className="text-left">
                    <div className="text-xs">Get it on</div>
                    <div className="text-lg font-bold">Google Play</div>
                  </div>
                </a>
              </div>
              
              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-blue-300">{downloadStats.totalDownloads.toLocaleString()}+</div>
                  <div className="text-sm text-blue-200">Downloads</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-300">{downloadStats.activeUsers.toLocaleString()}+</div>
                  <div className="text-sm text-blue-200">Active Users</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-300">{downloadStats.avgRating}</div>
                  <div className="text-sm text-blue-200">App Rating</div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20smartphone%20mockup%20showing%20real%20estate%20app%20interface%20AI%20staging%20tools%20property%20management%20dark%20theme%20professional%20mobile%20app%20design&width=400&height=800&seq=hero-phone&orientation=portrait"
                  alt="AI Realty Pro Mobile App"
                  className="w-full max-w-sm mx-auto"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted by Companies */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <p className="text-center text-gray-600 mb-8">Trusted by leading real estate companies</p>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            {companies.map((company, index) => (
              <div key={index} className="flex items-center space-x-2">
                <i className={`${company.logo} text-2xl text-gray-400`}></i>
                <span className="text-gray-600 font-medium">{company.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Everything You Need</h2>
            <p className="text-xl text-gray-600">Powerful AI tools designed for real estate professionals</p>
          </div>
          
          <div className="flex justify-center mb-12">
            <div className="bg-white rounded-xl p-2 shadow-lg">
              <div className="flex space-x-2">
                <button
                  onClick={() => setActiveTab('features')}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                    activeTab === 'features' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-600 hover:text-blue-600'
                  }`}
                >
                  Features
                </button>
                <button
                  onClick={() => setActiveTab('screenshots')}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                    activeTab === 'screenshots' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-600 hover:text-blue-600'
                  }`}
                >
                  Screenshots
                </button>
                <button
                  onClick={() => setActiveTab('reviews')}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                    activeTab === 'reviews' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-600 hover:text-blue-600'
                  }`}
                >
                  Reviews
                </button>
              </div>
            </div>
          </div>

          {/* Features Tab */}
          {activeTab === 'features' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${feature.icon} text-blue-600 text-xl`}></i>
                  </div>
                  <div className="mb-2">
                    <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                      {feature.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          )}

          {/* Screenshots Tab */}
          {activeTab === 'screenshots' && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-8">
                {screenshots.map((screenshot) => (
                  <div key={screenshot.id} className="bg-white rounded-xl p-4 shadow-lg">
                    <div className="aspect-[9/16] rounded-lg overflow-hidden mb-4">
                      <img 
                        src={screenshot.image} 
                        alt={screenshot.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h4 className="font-semibold text-gray-900 text-center">{screenshot.title}</h4>
                  </div>
                ))}
              </div>
              
              <div className="text-center">
                <p className="text-gray-600 mb-6">Experience the full power of AI Realty Pro on your mobile device</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="#" 
                    className="inline-flex items-center justify-center bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                  >
                    <i className="ri-apple-line mr-2"></i>
                    Download for iOS
                  </a>
                  <a 
                    href="#" 
                    className="inline-flex items-center justify-center bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                  >
                    <i className="ri-google-play-line mr-2"></i>
                    Download for Android
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                {reviews.map((review) => (
                  <div key={review.id} className="bg-white rounded-xl p-6 shadow-lg">
                    <div className="flex items-center mb-4">
                      <img 
                        src={review.avatar} 
                        alt={review.name}
                        className="w-12 h-12 rounded-full object-cover mr-4"
                      />
                      <div>
                        <h4 className="font-semibold text-gray-900">{review.name}</h4>
                        <p className="text-sm text-gray-600">{review.role}</p>
                        <p className="text-xs text-blue-600">{review.company}</p>
                      </div>
                    </div>
                    
                    <div className="flex mb-4">
                      {[...Array(review.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-yellow-400"></i>
                      ))}
                    </div>
                    
                    <p className="text-gray-700 italic">"{review.review}"</p>
                  </div>
                ))}
              </div>
              
              <div className="text-center bg-blue-50 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Join Thousands of Satisfied Users</h3>
                <p className="text-gray-600 mb-6">Download AI Realty Pro today and transform your real estate business</p>
                <div className="flex items-center justify-center space-x-6 mb-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">4.8</div>
                    <div className="text-sm text-gray-600">App Store Rating</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">4.9</div>
                    <div className="text-sm text-gray-600">Google Play Rating</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">10K+</div>
                    <div className="text-sm text-gray-600">5-Star Reviews</div>
                  </div>
                </div>
                <Button className="bg-blue-600 text-white hover:bg-blue-700">
                  <i className="ri-download-line mr-2"></i>
                  Download Now
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Features Showcase */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">AI-Powered Virtual Staging</h2>
              <p className="text-xl text-gray-600 mb-8">
                Transform empty properties into beautifully furnished spaces with our advanced AI technology. 
                Choose from multiple design styles and see instant results.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-green-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">Multiple design styles available</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-green-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">Instant results in seconds</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-green-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">High-resolution output</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-green-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">Works on mobile and desktop</span>
                </div>
              </div>
              
              <Link to="/dashboard">
                <Button className="bg-blue-600 text-white hover:bg-blue-700">
                  <i className="ri-magic-line mr-2"></i>
                  Try AI Staging
                </Button>
              </Link>
            </div>
            
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=before%20after%20comparison%20empty%20living%20room%20vs%20AI%20staged%20furnished%20room%20modern%20furniture%20virtual%20staging%20real%20estate&width=600&height=400&seq=staging-demo&orientation=landscape"
                alt="AI Virtual Staging Demo"
                className="w-full rounded-xl shadow-2xl"
              />
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2">
                <span className="text-sm font-semibold text-gray-900">Before</span>
              </div>
              <div className="absolute top-4 right-4 bg-blue-600/90 backdrop-blur-sm rounded-lg px-3 py-2">
                <span className="text-sm font-semibold text-white">After AI Staging</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of real estate professionals who are already using AI Realty Pro to close more deals and impress clients.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a 
              href="#" 
              className="inline-flex items-center justify-center bg-black text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-800 transition-colors"
            >
              <i className="ri-apple-line text-2xl mr-3"></i>
              <div className="text-left">
                <div className="text-xs">Download on the</div>
                <div className="text-lg font-bold">App Store</div>
              </div>
            </a>
            
            <a 
              href="#" 
              className="inline-flex items-center justify-center bg-black text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-800 transition-colors"
            >
              <i className="ri-google-play-line text-2xl mr-3"></i>
              <div className="text-left">
                <div className="text-xs">Get it on</div>
                <div className="text-lg font-bold">Google Play</div>
              </div>
            </a>
          </div>
          
          <p className="text-blue-200 text-sm">
            Free to download • Premium features available • No credit card required
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
